import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  ComposedChart,
  Bar,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getHistory, removeHistoryEntry, clearHistory, HistoryEntry } from '../../utils/historyStorage';
import StatisticsView from '../StatisticsView/StatisticsView';
import { useOptimization } from '../../context/OptimizationContext';
import ComparisonView from '../ComparisonView/ComparisonView';

interface HistoryViewProps {
  onSelectResult?: (entry: HistoryEntry) => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ onSelectResult }) => {
  const { results, isRunning, showNotification } = useOptimization();
  const [history, setHistory] = useState<HistoryEntry[]>(getHistory());
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);

  const refreshHistory = () => {
    setHistory(getHistory());
  };

  // Refresh history on mount, when results change, and when optimization completes
  useEffect(() => {
    refreshHistory();
  }, [results]); // Refresh when results change

  // Also refresh when optimization stops (completes)
  useEffect(() => {
    if (!isRunning && results) {
      // Small delay to ensure localStorage has been updated
      setTimeout(() => {
        refreshHistory();
      }, 500);
    }
  }, [isRunning, results]);

  const handleView = (entry: HistoryEntry) => {
    setSelectedEntry(entry);
    setViewDialogOpen(true);
    if (onSelectResult) {
      onSelectResult(entry);
    }
  };

  const handleDelete = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setEntryToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (entryToDelete) {
      removeHistoryEntry(entryToDelete);
      refreshHistory();
      setEntryToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleClearAll = () => {
    setClearAllConfirmOpen(true);
  };

  const confirmClearAll = () => {
    clearHistory();
    refreshHistory();
    setClearAllConfirmOpen(false);
    showNotification('All history cleared successfully', 'success');
  };

  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [history]);

  // Prepare comprehensive chart data for selected entry
  const comprehensiveChartData = useMemo(() => {
    if (!selectedEntry) return [];
    
    return [
      { key: 'ga', name: 'GA', result: selectedEntry.results.ga },
      { key: 'culture', name: 'Cultural', result: selectedEntry.results.culture },
      { key: 'frog', name: 'SFLA', result: selectedEntry.results.frog },
      { key: 'sflaga', name: 'SFLAGA', result: selectedEntry.results.sflaga },
      { key: 'sflaca', name: 'SFLACA', result: selectedEntry.results.sflaca },
      { key: 'pso', name: 'PSO', result: selectedEntry.results.pso },
      { key: 'gapso', name: 'GAPSO', result: selectedEntry.results.gapso },
    ].map(({ name, result }) => ({
      name,
      Fitness: result.fitness,
      Energy: result.energy,
      Cost: result.cost,
      ResponseTime: result.responseTime,
      ExecutionTime: result.executionTime || 0,
    }));
  }, [selectedEntry]);

  const chartColors = {
    ga: '#1976d2',
    culture: '#9c27b0',
    frog: '#ed6c02',
    sflaga: '#2e7d32',
    pso: '#d32f2f',
    gapso: '#0288d1',
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Results History
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<CompareArrowsIcon />}
            onClick={() => setComparisonOpen(true)}
            disabled={history.length < 2}
            sx={{ mr: 1 }}
          >
            Compare
          </Button>
          <IconButton onClick={refreshHistory} size="small" sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleClearAll}
            disabled={history.length === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {sortedHistory.length === 0 ? (
        <Alert severity="info">
          No results history available. Run optimizations to see them here.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Timestamp</strong></TableCell>
                <TableCell align="right"><strong>Tasks</strong></TableCell>
                <TableCell align="right"><strong>Population</strong></TableCell>
                <TableCell align="right"><strong>Runs</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHistory.map((entry) => (
                <TableRow
                  key={entry.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleView(entry)}
                >
                  <TableCell>
                    {new Date(entry.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.configuration.taskCount}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.configuration.initialPopulation}
                  </TableCell>
                  <TableCell align="right">
                    {entry.results.runNumber ? (
                      <Chip label={entry.results.runNumber} size="small" color="primary" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(entry.id, e)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Results Details - {selectedEntry && new Date(selectedEntry.timestamp).toLocaleString()}
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Configuration:</strong> Tasks={selectedEntry.results.configuration.taskCount}, 
                  Properties={selectedEntry.results.configuration.propertyCount}, 
                  Population={selectedEntry.results.configuration.initialPopulation}
                  {selectedEntry.results.runNumber && (
                    <> • <strong>Runs:</strong> {selectedEntry.results.runNumber}</>
                  )}
                </Typography>
              </Alert>
              
              {/* Results Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Results Details
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Algorithm</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Fitness</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Energy (J)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Response Time (ms)</TableCell>
                        {selectedEntry.results.ga.executionTime && (
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Execution Time (ms)</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const algorithms = [
                          { key: 'ga', name: 'GA', result: selectedEntry.results.ga },
                          { key: 'culture', name: 'Cultural', result: selectedEntry.results.culture },
                          { key: 'frog', name: 'SFLA', result: selectedEntry.results.frog },
                          { key: 'sflaga', name: 'SFLAGA', result: selectedEntry.results.sflaga },
                          { key: 'sflaca', name: 'SFLACA', result: selectedEntry.results.sflaca },
                          { key: 'pso', name: 'PSO', result: selectedEntry.results.pso },
                          { key: 'gapso', name: 'GAPSO', result: selectedEntry.results.gapso },
                        ];

                        // Calculate best and worst values for each metric
                        const fitnessValues = algorithms.map(a => a.result.fitness);
                        const maxFitness = Math.max(...fitnessValues);
                        const minFitness = Math.min(...fitnessValues);

                        const energyValues = algorithms.map(a => a.result.energy);
                        const minEnergy = Math.min(...energyValues);
                        const maxEnergy = Math.max(...energyValues);

                        const costValues = algorithms.map(a => a.result.cost);
                        const minCost = Math.min(...costValues);
                        const maxCost = Math.max(...costValues);

                        const responseTimeValues = algorithms.map(a => a.result.responseTime);
                        const minResponseTime = Math.min(...responseTimeValues);
                        const maxResponseTime = Math.max(...responseTimeValues);

                        const executionTimeValues = selectedEntry.results.ga.executionTime 
                          ? algorithms.map(a => a.result.executionTime || Infinity).filter(v => v !== Infinity)
                          : [];
                        const minExecutionTime = executionTimeValues.length > 0 ? Math.min(...executionTimeValues) : Infinity;
                        const maxExecutionTime = executionTimeValues.length > 0 ? Math.max(...executionTimeValues) : -Infinity;

                        return algorithms.map(({ key, name, result }) => {
                          const isBestFitness = result.fitness === maxFitness && maxFitness !== minFitness;
                          const isWorstFitness = result.fitness === minFitness && maxFitness !== minFitness;

                          const isBestEnergy = result.energy === minEnergy && maxEnergy !== minEnergy;
                          const isWorstEnergy = result.energy === maxEnergy && maxEnergy !== minEnergy;

                          const isBestCost = result.cost === minCost && maxCost !== minCost;
                          const isWorstCost = result.cost === maxCost && maxCost !== minCost;

                          const isBestResponseTime = result.responseTime === minResponseTime && maxResponseTime !== minResponseTime;
                          const isWorstResponseTime = result.responseTime === maxResponseTime && maxResponseTime !== minResponseTime;

                          const isBestExecutionTime = selectedEntry.results.ga.executionTime && result.executionTime === minExecutionTime && maxExecutionTime !== minExecutionTime;
                          const isWorstExecutionTime = selectedEntry.results.ga.executionTime && result.executionTime === maxExecutionTime && maxExecutionTime !== minExecutionTime;

                          return (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 500 }}>{name}</TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  color: isBestFitness ? 'success.main' : isWorstFitness ? 'error.main' : 'text.primary',
                                  fontWeight: isBestFitness || isWorstFitness ? 700 : 400,
                                }}
                              >
                                {result.fitness.toFixed(6)}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  color: isBestEnergy ? 'success.main' : isWorstEnergy ? 'error.main' : 'text.primary',
                                  fontWeight: isBestEnergy || isWorstEnergy ? 700 : 400,
                                }}
                              >
                                {result.energy.toFixed(6)}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  color: isBestCost ? 'success.main' : isWorstCost ? 'error.main' : 'text.primary',
                                  fontWeight: isBestCost || isWorstCost ? 700 : 400,
                                }}
                              >
                                {result.cost.toFixed(6)}
                              </TableCell>
                              <TableCell 
                                align="right"
                                sx={{
                                  color: isBestResponseTime ? 'success.main' : isWorstResponseTime ? 'error.main' : 'text.primary',
                                  fontWeight: isBestResponseTime || isWorstResponseTime ? 700 : 400,
                                }}
                              >
                                {result.responseTime.toFixed(2)}
                              </TableCell>
                              {selectedEntry.results.ga.executionTime && (
                                <TableCell 
                                  align="right"
                                  sx={{
                                    color: isBestExecutionTime ? 'success.main' : isWorstExecutionTime ? 'error.main' : 'text.primary',
                                    fontWeight: isBestExecutionTime || isWorstExecutionTime ? 700 : 400,
                                  }}
                                >
                                  {result.executionTime ? result.executionTime.toFixed(2) : '-'}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Comprehensive Performance Chart */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Comprehensive Performance Analysis
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {comprehensiveChartData.length > 0 && (
                      <ResponsiveContainer width="100%" height={450}>
                        <ComposedChart data={comprehensiveChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis 
                            yAxisId="left" 
                            label={{ value: 'Fitness', angle: -90, position: 'insideLeft' }} 
                          />
                          <YAxis 
                            yAxisId="right" 
                            orientation="right" 
                            label={{ 
                              value: selectedEntry.results.ga.executionTime !== undefined
                                ? 'Energy, Cost, Response Time, Execution Time' 
                                : 'Energy, Cost, Response Time', 
                              angle: 90, 
                              position: 'insideRight' 
                            }} 
                          />
                          <Tooltip 
                            formatter={(value: number, name: string) => {
                              if (name === 'Fitness') return value.toFixed(6);
                              if (name === 'Energy (Joules)' || name === 'Cost') return value.toFixed(6);
                              if (name.includes('Time')) return `${value.toFixed(2)} ms`;
                              return value.toFixed(2);
                            }}
                          />
                          <Legend />
                          <Bar 
                            yAxisId="left" 
                            dataKey="Fitness" 
                            fill={chartColors.ga} 
                            name="Fitness" 
                          />
                          <Area 
                            yAxisId="right" 
                            type="monotone" 
                            dataKey="Energy" 
                            fill={chartColors.culture} 
                            fillOpacity={0.6} 
                            name="Energy (Joules)" 
                          />
                          <Area 
                            yAxisId="right" 
                            type="monotone" 
                            dataKey="Cost" 
                            fill={chartColors.frog} 
                            fillOpacity={0.6} 
                            name="Cost" 
                          />
                          <Line 
                            yAxisId="right" 
                            type="monotone" 
                            dataKey="ResponseTime" 
                            stroke={chartColors.sflaga} 
                            strokeWidth={2} 
                            name="Response Time (ms)" 
                          />
                          {selectedEntry.results.ga.executionTime !== undefined && (
                            <Line 
                              yAxisId="right" 
                              type="monotone" 
                              dataKey="ExecutionTime" 
                              stroke={chartColors.pso} 
                              strokeWidth={2} 
                              strokeDasharray="5 5"
                              name="Execution Time (ms)" 
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      This comprehensive chart displays all key performance metrics side-by-side for each algorithm. 
                      <strong> Fitness</strong> (bar chart, left axis) shows solution quality - higher is better. 
                      <strong> Energy</strong> and <strong>Cost</strong> (area charts, right axis) show resource efficiency - lower is better. 
                      <strong> Response Time</strong> (line chart, right axis) shows latency - lower is better.
                      {selectedEntry.results.ga.executionTime !== undefined && (
                        <> <strong> Execution Time</strong> (dashed line, right axis) shows algorithm speed - lower is better.</>
                      )}
                    </Typography>
                  </Alert>
                </Box>
              </Box>

              {/* Statistics */}
              {selectedEntry.results.statistics && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Statistics
                  </Typography>
                  <StatisticsView results={selectedEntry.results} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Result</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this result from history?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={clearAllConfirmOpen} onClose={() => setClearAllConfirmOpen(false)}>
        <DialogTitle>Clear All History</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Are you sure you want to clear all history?
            </Typography>
            <Typography variant="body2">
              This action cannot be undone. All {history.length} result{history.length !== 1 ? 's' : ''} will be permanently deleted.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmClearAll} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comparison Dialog */}
      <ComparisonView 
        open={comparisonOpen} 
        onClose={() => setComparisonOpen(false)}
        onError={(message) => showNotification(message, 'error')}
      />
    </Box>
  );
};

export default HistoryView;

